import {
  useWSSnackbar,
  WSActions,
  WSCard,
  WSGrid,
  WSList
} from "@wingspanhq/fe-component-library";
import { RequirementDefinition } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { DataItem } from "../../../../../shared/components/DataItem";
import { ErrorAlert } from "../../../../../v3/components/ErrorAlert";
import { RequirementInstructionsView } from "../../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { useConfirmCreateRequirementModal } from "../../../components/CreateRequirementDefinitionForm/useConfirmCreateRequirementModal";
import { useCreateRequirementDefinitionSettings } from "../../../hooks/useCreateRequirementDefinitionSettings";
import { RequirementDefinitionFormValues } from "../../../types";
import { InsuranceInstructionsView } from "../components/InsuranceInstructionsView";
import { RequirementDefinitionTemplate } from "../types";

type Props = {
  onBack: () => void;
  onSuccess: (response: RequirementDefinition) => void;
  formData: RequirementDefinitionFormValues;
  template: RequirementDefinitionTemplate;
  engagementId?: string;
};

export const StepReviewAndCreate: React.FC<Props> = ({
  template,
  engagementId,
  onSuccess,
  onBack,
  formData
}) => {
  const { openSnackbar } = useWSSnackbar();
  const confirmCreateRequirementModal = useConfirmCreateRequirementModal();

  const successMessage = () => {
    template.form?.successCreationMessage &&
      openSnackbar({
        message: template.form?.successCreationMessage,
        theme: "dark",
        type: "success"
      });
  };

  const { name } = formData;
  const acceptWingspanMasterPolicy =
    "acceptWingspanMasterPolicy" in formData
      ? formData.acceptWingspanMasterPolicy
      : undefined;

  const { dataSourceError, submit, isLoading } =
    useCreateRequirementDefinitionSettings();

  return (
    <WSList gap="2XL">
      <WSCard header="Requirement preview">
        <WSGrid gutter={{ m: "2XL" }}>
          <WSGrid.Item span={{ m: "6" }}>
            <DataItem label="Name" value={name} />
          </WSGrid.Item>

          <WSGrid.Item span={{ m: "6" }}>
            <DataItem
              icon={template.icon}
              label="Type"
              secondaryText={template.helperText}
              value={template.name}
            />
          </WSGrid.Item>
        </WSGrid>
      </WSCard>

      {template.instructions || formData.instructions || template.isPolicy ? (
        <WSCard
          header={{
            label: {
              helperText:
                "The following instructions will be shown to the contractors.",
              text: "Instructions to contractors"
            }
          }}
        >
          <WSList gap="2XL">
            {template.isPolicy ? (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={template.name}
                policyTypes={template.policyType}
              />
            ) : null}
            {!template.isPolicy &&
            (template.instructions || formData.instructions) ? (
              <WSCard>
                <RequirementInstructionsView
                  instructions={template.instructions || formData.instructions!}
                />
              </WSCard>
            ) : null}
          </WSList>
        </WSCard>
      ) : null}

      <ErrorAlert context="Other" error={dataSourceError} />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Tertiary",
            label: "Back",
            onClick: onBack,
            type: "button"
          },
          {
            label: "Create requirement",
            loading: isLoading,
            onAsyncClick: async () => {
              let response: Awaited<ReturnType<typeof submit>>;

              try {
                response = await submit({ formData, template });
              } catch {
                // The data-source create failed. Its error is already on screen
                // via `dataSourceError` above, read from the mutation's own
                // state — so stop here instead of letting the rejection escape:
                // WSButton re-throws whatever `onAsyncClick` rejects with, and
                // an unhandled rejection is all the user would get from it.
                return;
              }

              confirmCreateRequirementModal.open({
                ...response,
                engagementId,
                onSuccess(requirement) {
                  successMessage();
                  onSuccess?.(requirement);
                }
              });
            },
            type: "submit"
          }
        ]}
      />
    </WSList>
  );
};
